import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Input, AfterViewInit, Renderer2, ElementRef, ViewChild, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, of, merge } from 'rxjs';
import { takeUntil, map, distinctUntilChanged, filter, tap, switchMap, shareReplay, startWith, take } from 'rxjs/operators';
import { ofType } from '@ngrx/effects';
import * as TemplateActions from '../../ngrx/template/template.actions';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { ComponentRegistryService } from '../../services/component-registry/component-registry.service';
import { CommonModule } from '@angular/common';
import { DynamicComponentComponent } from '../dynamic-component/dynamic-component.component';
import { LetDirective } from '@ngrx/component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AppState } from '../../ngrx/template/template.reducers';
import * as fromTemplate from '../../ngrx/template/template.selectors';
import { Actions } from '@ngrx/effects';
import { TemplateService } from '../../services/template/template.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CustomSlug } from '../../models/CustomSlug.model';
import { CustomComponent } from '../../models/CustomComponent.model';
import { MODE_FALLBACK_ORDER, ResponsiveMode } from '../../constants/responsive-modes';
import { environment } from '../../environment/environment';
import { AuthService } from '../../services/auth/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { PermissionEvaluatorService } from '../../services/component-permission/permission-evaluator.service';
import { SplashScreenService } from '../../services/splash-screen/splash-screen.service';


interface SlugData {
  staticTemplate?: CustomTemplate;
  dynamicTemplate?: CustomTemplate;
  customSlug?: CustomSlug;
  loaded: boolean;
  lazyLoaded: boolean;
  lazyLoadedData: any;
}

interface SlugsState {
  [key: string]: SlugData;
}

@Component({
  selector: 'app-dynamic-template',
  templateUrl: './dynamic-template.component.html',
  styleUrls: ['./dynamic-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, DynamicComponentComponent, LetDirective, MatSnackBarModule]
})
export class DynamicTemplateComponent implements OnInit, OnDestroy, AfterViewInit {
  staticTemplate$: Observable<CustomTemplate | null>;
  dynamicTemplate$: Observable<CustomTemplate | null>;
  customSlug$: Observable<CustomSlug | null>;
  currentSlug$: Observable<string>;

  private destroy$ = new Subject<void>();
  private templateUpdate$ = new Subject<void>();
  private lastStaticTemplateUuid: string | undefined;
  private lastStaticTemplate: CustomTemplate | null = null;

  @Input() slug: string = '';
  @Input() dynamicOnly: boolean = false;


  private readonly GRID_ROW_HEIGHT = 100; // pixels

  private memoizedFilterResults = new Map<string, CustomComponent[]>();
  private memoizedFilterTemplates = new Map<string, CustomTemplate | null | undefined>();

  private tabletDesignWidth = environment.tabletWidth;
  private mobileDesignWidth = environment.mobileWidth;
  
  private mobileScalingEndViewport = environment.mobileWidth;
  private scalingUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
  private scalingRetryTimeout: ReturnType<typeof setTimeout> | null = null;
  private scalingRetryCount = 0;
  private scalableHostResizeObserver: ResizeObserver | null = null;
  private scalableHostMutationObserver: MutationObserver | null = null;
  private readonly maxScalingRetries = 60;



  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    public componentRegistry: ComponentRegistryService,
    private breakpointObserver: BreakpointObserver,
    private actions$: Actions,
    public templateService: TemplateService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private renderer: Renderer2,
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private permissionEvaluator: PermissionEvaluatorService,
    private splashScreenService: SplashScreenService
  ) {
    this.currentSlug$ = this.route.url.pipe(
      map(segments => {
        if (this.slug) {
          return this.slug;
        }
        if (segments.length > 0) {
          // Join all segments with forward slashes
          return segments.map(s => s.path).join('/');
        }
        return 'default';
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.customSlug$ = this.currentSlug$.pipe(
      switchMap(slug => 
        this.store.select((state: AppState) => state.template.slugs[slug]?.customSlug || null)
      ),
      shareReplay(1)
    );

    const slugState$ = combineLatest([
      this.currentSlug$,
      this.templateService.currentMode$,
      this.store.select((state: AppState) => state.template.slugs as SlugsState)
    ]).pipe(
      distinctUntilChanged(([prevSlug], [currSlug]) => prevSlug === currSlug),
      tap(([slug, currentMode, slugs]) => this.processTemplateData(slug, currentMode, slugs)),
      switchMap(([slug, currentMode, slugs]) => {
        if (!slugs[slug]?.loaded) {
          return this.actions$.pipe(
            ofType(TemplateActions.loadPageDataSuccess, TemplateActions.loadPageDataUnchanged),
            filter(action => action.slug === slug),
            take(1),
            switchMap(() => 
              this.store.select((state: AppState) => state.template.slugs as SlugsState).pipe(
                filter(slugs => slugs[slug]?.loaded),
                take(1),
                map(updatedSlugs => [slug, currentMode, updatedSlugs] as const)
              )
            )
          );
        }
        return of([slug, currentMode, slugs] as const);
      }),
      shareReplay(1)
    );

    this.staticTemplate$ = slugState$.pipe(
      map(([slug, currentMode, slugs]) => {
        const slugData = slugs[slug];
        if (!slugData?.loaded) {
          return undefined;
        }

        if (!slugData?.customSlug) {
          return undefined;
        }

        const templateUuid = slugData.customSlug.getStaticTemplateFromMode(currentMode as 1 | 2 | 3);
        
        if (!templateUuid) {
          return undefined;
        }

        return {
          slug,
          currentMode: currentMode as 1 | 2 | 3,
          templateUuid
        };
      }),
      filter((data): data is { slug: string; currentMode: 1 | 2 | 3; templateUuid: string } => !!data),
      distinctUntilChanged((prev, curr) => 
        prev.slug === curr.slug && 
        prev.currentMode === curr.currentMode && 
        prev.templateUuid === curr.templateUuid
      ),
      switchMap(data => 
        this.store.select(
          fromTemplate.createSelectStaticTemplate(this.componentRegistry)({ 
            uuid: data.templateUuid, 
            slug: data.slug,
            currentMode: data.currentMode
          })
        ).pipe(take(1))
      ),
      tap(template => {
        if (template) {
          this.lastStaticTemplate = template;
        }
      })
    );

    this.dynamicTemplate$ = this.currentSlug$.pipe(
      switchMap(slug => 
        combineLatest([
          of(slug),
          this.templateService.currentMode$,
          this.store.select((state: AppState) => state.template.slugs as SlugsState)
        ]).pipe(
          switchMap(([currentSlug, currentMode, slugs]) => {
            const slugData = slugs[currentSlug];
            
            if (!slugData?.loaded) {
              return this.actions$.pipe(
                ofType(TemplateActions.loadPageDataSuccess, TemplateActions.loadPageDataUnchanged),
                filter(action => action.slug === currentSlug),
                take(1),
                switchMap(() => 
                  this.store.select((state: AppState) => state.template.slugs as SlugsState).pipe(
                    take(1),
                    map(updatedSlugs => [currentSlug, currentMode, updatedSlugs] as const)
                  )
                )
              );
            }
            
            return of([currentSlug, currentMode, slugs] as const);
          }),
          take(1)
        )
      ),
      map(([slug, currentMode, slugs]) => {
        const slugData = slugs[slug];
        const templateUuid = slugData?.customSlug?.getDynamicTemplateFromMode(currentMode as 1 | 2 | 3);
        
        if (!templateUuid) {
          return undefined;
        }

        return {
          slug,
          currentMode: currentMode as 1 | 2 | 3,
          templateUuid
        };
      }),
      filter((data): data is { slug: string; currentMode: 1 | 2 | 3; templateUuid: string } => !!data),
      distinctUntilChanged((prev, curr) => 
        prev.slug === curr.slug && 
        prev.currentMode === curr.currentMode && 
        prev.templateUuid === curr.templateUuid
      ),
      switchMap(data => 
        this.store.select(
          fromTemplate.createSelectDynamicTemplate(this.componentRegistry)({ 
            uuid: data.templateUuid, 
            slug: data.slug,
            currentMode: data.currentMode
          })
        ).pipe(take(1))
      ),
      shareReplay(1)
    );
  }

  ngOnInit() {
    // CSS handles autoHeight automatically - no JavaScript needed!
    
    this.permissionEvaluator.onPermissionVisibilityChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());

    this.authService.authStateReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ready) => {
        if (ready) {
          this.cdr.markForCheck();
        }
      });

    this.dynamicTemplate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.permissionEvaluator.clearPermissionVisibilityCache());

    if (isPlatformBrowser(this.platformId)) {
      this.setupScalingTriggers();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (isPlatformBrowser(this.platformId) && !this.dynamicOnly) {
      document.documentElement.classList.remove('duna-app-shell');
      document.body.classList.remove('duna-app-shell');
      document.documentElement.classList.remove('duna-tabs-no-scale');
      document.body.classList.remove('duna-tabs-no-scale');
    }
    
    // Clean up resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.scalingUpdateTimeout) {
      clearTimeout(this.scalingUpdateTimeout);
    }

    if (this.scalingRetryTimeout) {
      clearTimeout(this.scalingRetryTimeout);
    }

    this.scalableHostResizeObserver?.disconnect();
    this.scalableHostMutationObserver?.disconnect();

    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);
      window.removeEventListener('pageshow', this.onPageShow);
      window.visualViewport?.removeEventListener('resize', this.onVisualViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onVisualViewportChange);
      document.removeEventListener('duna-request-scaling-update', this.onScalingUpdateRequest);
    }

    // Clean up ResizeObservers
    if (isPlatformBrowser(this.platformId)) {
      const gridItems = this.el.nativeElement.querySelectorAll('.grid-item');
      gridItems.forEach((gridItem: HTMLElement) => {
        const resizeObserver = (gridItem as any)._resizeObserver;
        if (resizeObserver) {
          resizeObserver.disconnect();
          delete (gridItem as any)._resizeObserver;
        }
        
        // Clean up resize timeout
        const resizeTimeout = (gridItem as any)._resizeTimeout;
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
          delete (gridItem as any)._resizeTimeout;
        }

        // Clean up MutationObserver
        const mutationObserver = (gridItem as any)._mutationObserver;
        if (mutationObserver) {
          mutationObserver.disconnect();
          delete (gridItem as any)._mutationObserver;
        }

        // Clean up mutation timeout
        const mutationTimeout = (gridItem as any)._mutationTimeout;
        if (mutationTimeout) {
          clearTimeout(mutationTimeout);
          delete (gridItem as any)._mutationTimeout;
        }
      });
    }


  }

  /**
   * Public method to force scaling recalculation
   * Can be called from parent components or services
   */
  public forceScalingUpdate(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.templateService.recalculateMode();
      this.scheduleScalingUpdate({ immediate: true });
    }
  }



  trackByComponentUuid(component: CustomComponent): string {
    return (
      component.uuid ||
      `${component.name}-${component.mode}-${component.x}-${component.y}`
    );
  }

  filterComponentsByMode(template: CustomTemplate | undefined | null, mode: 1 | 2 | 3) {
    const cacheKey = `${template?.uuid}-${mode}`;

    if (this.memoizedFilterTemplates.get(cacheKey) !== template) {
      this.memoizedFilterResults.delete(cacheKey);
      this.memoizedFilterTemplates.set(cacheKey, template);
    }

    const cached = this.memoizedFilterResults.get(cacheKey);
    if (cached) {
      return cached;
    }

    if (!template) {
      const empty: CustomComponent[] = [];
      this.memoizedFilterResults.set(cacheKey, empty);
      return empty;
    }

    const componentsForMode = template.customComponents.filter(
      (component) => component.mode === mode
    );
    if (componentsForMode.length > 0) {
      this.memoizedFilterResults.set(cacheKey, componentsForMode);
      return componentsForMode;
    }

    for (const fallbackMode of MODE_FALLBACK_ORDER[mode]) {
      const fallbackComponents = template.customComponents.filter(
        (component) => component.mode === fallbackMode
      );
      if (fallbackComponents.length > 0) {
        this.memoizedFilterResults.set(cacheKey, fallbackComponents);
        return fallbackComponents;
      }
    }

    const empty: CustomComponent[] = [];
    this.memoizedFilterResults.set(cacheKey, empty);
    return empty;
  }

  calculateMainComponentSpan(dynamicTemplate: CustomTemplate | undefined | null, currentMode: 1 | 2 | 3): number {
    if (!dynamicTemplate) return 1;

    const components = this.filterComponentsByMode(dynamicTemplate, currentMode);

    const maxY = Math.max(...components.map(comp => (comp.y || 0) + (comp.rows || 1)));
    return Math.max(maxY, 1) + 3; // Ensure we always return at least 1
  }

  ngAfterViewInit() {
    // CSS handles autoHeight automatically - no JavaScript needed!
    // Add fallback class for browsers without :has() support
    this.addAutoHeightFallbackClasses();
    
    if (isPlatformBrowser(this.platformId)) {
      this.setupScalableHostMutationObserver();
      this.scheduleScalingUpdate();

      // Periodically check for auto-height components to ensure proper attribute application
      setTimeout(() => {
        this.addAutoHeightFallbackClasses();
      }, 500);
      
      setTimeout(() => {
        this.addAutoHeightFallbackClasses();
      }, 1000);
    }
  }

  private addAutoHeightFallbackClasses(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.syncAppShellLayout();

      // Use a timeout to ensure DOM is fully rendered
      setTimeout(() => {
        // Check if there are any auto-height components
        const autoHeightComponents = this.el.nativeElement.querySelectorAll('[data-auto-height="true"]');

        if (autoHeightComponents.length > 0) {
          // Add fallback class to all grid containers
          const gridContainers = this.el.nativeElement.querySelectorAll('.grid-container');
          gridContainers.forEach((container: HTMLElement) => {
            container.classList.add('has-auto-height');
          });
          
          // Add fallback class to template containers
          const templateContainers = this.el.nativeElement.querySelectorAll('.template-container, .dynamic-template-container');
          templateContainers.forEach((container: HTMLElement) => {
            container.classList.add('has-auto-height');
          });
          
          // Also check for main component and apply auto-height attributes
          const mainComponent = document.querySelector('app-main');
          const mainContent = document.querySelector('#main');
          if (mainComponent) {
            mainComponent.setAttribute('data-auto-height', 'true');
            mainComponent.classList.add('has-auto-height');
          }
          if (mainContent) {
            mainContent.setAttribute('data-auto-height', 'true');
            mainContent.classList.add('has-auto-height');
          }
          
          // Apply auto-height attributes to body for proper scrolling
          this.applyAutoHeightToBody(true);
        } else {
          this.applyAutoHeightToBody(false);
        }

        this.syncAppShellLayout();
      }, 100);
    }
  }

  private syncAppShellLayout(): void {
    if (!isPlatformBrowser(this.platformId) || this.dynamicOnly) {
      return;
    }

    const html = document.documentElement;
    const body = document.body;
    const hasNav = !!document.querySelector('[id*="NavComponent"][id*="_parent"]');
    const hasFooter = !!document.querySelector('[id*="FooterComponent"][id*="_parent"]');

    if (hasNav && hasFooter) {
      html.classList.add('duna-app-shell');
      body.classList.add('duna-app-shell');
      this.scheduleScalingUpdate();
      requestAnimationFrame(() => this.syncNavShellMetrics());
    } else {
      html.classList.remove('duna-app-shell');
      body.classList.remove('duna-app-shell');
      document.documentElement.style.removeProperty('--duna-nav-height');
    }
  }

  /** Sync --duna-nav-height with the rendered header; never collapse nav via inline styles. */
  private syncNavShellMetrics(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const navParent = document.querySelector('[id*="NavComponent"][id*="_parent"]') as HTMLElement | null;
    const header = navParent?.querySelector('header#header') as HTMLElement | null;

    if (!navParent || !header) {
      return;
    }

    // Clear any inline height overrides — CSS owns nav sizing via --duna-nav-height.
    this.renderer.removeStyle(navParent, 'height');
    this.renderer.removeStyle(navParent, 'min-height');
    this.renderer.removeStyle(navParent, 'max-height');

    const fallbackHeight = window.innerWidth <= 1320 ? 48 : 100;
    const measuredHeight = Math.round(header.getBoundingClientRect().height);
    const height = measuredHeight > 0 ? measuredHeight : fallbackHeight;

    document.documentElement.style.setProperty('--duna-nav-height', `${height}px`);
  }
  
  private applyAutoHeightToBody(hasAutoHeight: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      const body = document.body;
      const html = document.documentElement;
      const mainComponent = document.querySelector('app-main');
      const mainContent = document.querySelector('#main');
      
      if (hasAutoHeight) {
        if (!body.classList.contains('duna-app-shell')) {
          body.setAttribute('data-auto-height', 'true');
          html.setAttribute('data-auto-height', 'true');
        }
        if (mainComponent) {
          mainComponent.setAttribute('data-auto-height', 'true');
          mainComponent.classList.add('has-auto-height');
        }
        if (mainContent) {
          mainContent.setAttribute('data-auto-height', 'true');
          mainContent.classList.add('has-auto-height');
        }
      } else {
        body.removeAttribute('data-auto-height');
        html.removeAttribute('data-auto-height');
        if (mainComponent) {
          mainComponent.removeAttribute('data-auto-height');
          mainComponent.classList.remove('has-auto-height');
        }
        if (mainContent) {
          mainContent.removeAttribute('data-auto-height');
          mainContent.classList.remove('has-auto-height');
        }
      }
    }
  }

  getRowSpan(component: CustomComponent, element: HTMLElement): number {
    if (!component) {
      return 1;
    }

    // CSS handles autoHeight automatically - just return the configured rows
    return component.rows || 1;
  }

  onDynamicComponentInit(gridItem: HTMLElement, component: CustomComponent): void {
    // Ensure inputs are initialized by calling getOrSetInputs()
    const inputs = component.getOrSetInputs();

    if (inputs && inputs['autoHeight'] && inputs['autoHeight'].value) {
      this.addAutoHeightFallbackClasses();
    }
    
    // Re-check auto-height components and update body attributes
    setTimeout(() => {
      this.addAutoHeightFallbackClasses();
    }, 50);
  }



  getComponentStyles(component: any, gridItem: any) {
    return {
      'grid-column': ((component?.x || 0) + 1) + ' / span ' + (component?.cols || 1),
      'grid-row': ((component?.y || 0) + 1) + ' / span ' + this.getRowSpan(component, gridItem),
      ...(component.customCSS || {})
    };
  }

  getStaticComponentStyles(component: any, dynamicTemplate: any, currentMode: any) {
    return {
      'grid-column': ((component.x || 0) + 1) + ' / span ' + (component.cols || 1),
      'grid-row': component.name === 'MainComponent' 
        ? ((component.y || 0) + 1) + ' / span ' + this.calculateMainComponentSpan(dynamicTemplate, currentMode)
        : ((component.y || 0) + 1) + ' / span ' + (component.rows || 1),
      ...(component.customCSS || {})
    };
  }

  isComponentVisible(component: CustomComponent): boolean {
    if (!this.isComponentVisibleByRole(component)) {
      return false;
    }

    this.permissionEvaluator.ensurePermissionChecked(component, null, this.route);
    return this.permissionEvaluator.getCachedPermissionVisible(component);
  }

  private isComponentVisibleByRole(component: CustomComponent): boolean {
    const allowedRoles = component?.settings['allowedRoles']?.value;
    
    // Get user roles and auth state
    const userRoles = this.authService.getUserRoles();
    
    // SuperAdmin can always see everything (role gate only; permission may still apply)
    if (userRoles.includes('superadmin')) {
      return true;
    }
    
    // If no allowed roles specified, component is visible to all
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }

    // Check if user has ANY of the roles that ARE allowed to see the component
    return allowedRoles.some((role: string) => userRoles.includes(role));
  }

  private processTemplateData(slug: string, currentMode: number, slugs: any) {
    if (!slugs[slug]?.loaded) {
      this.store.dispatch(TemplateActions.loadPageDataBySlug({ slug }));
    }
  }

  private resizeTimeout: any;

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event): void {
    if (isPlatformBrowser(this.platformId)) {
      this.handleViewportChange();
    }
  }

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange(event?: Event): void {
    if (isPlatformBrowser(this.platformId)) {
      // Orientation change needs a longer delay as the viewport size updates after the event
      setTimeout(() => {
        this.handleViewportChange();
      }, 200);
    }
  }

  private handleViewportChange(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.templateService.recalculateMode();
    this.scheduleScalingUpdate({ immediate: true });

    this.resizeTimeout = setTimeout(() => {
      this.scheduleScalingUpdate();
    }, 100);
  }

  private setupScalingTriggers(): void {
    merge(
      this.staticTemplate$.pipe(filter((template): template is CustomTemplate => !!template)),
      this.dynamicTemplate$.pipe(filter((template): template is CustomTemplate => !!template))
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.scalingRetryCount = 0;
      this.scheduleScalingUpdate();
      this.syncAppShellLayout();
    });

    this.currentSlug$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.scalableHostResizeObserver?.disconnect();
      this.scalableHostResizeObserver = null;
      this.scalingRetryCount = 0;
      this.scheduleScalingUpdate();
    });

    this.templateService.currentMode$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => this.scheduleScalingUpdate());

    this.templateService.modeRecalculated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.scheduleScalingUpdate());

    this.splashScreenService.showSplashScreen$.pipe(
      filter(visible => !visible),
      takeUntil(this.destroy$)
    ).subscribe(() => this.scheduleScalingUpdate());

    document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);
    window.addEventListener('pageshow', this.onPageShow);
    window.visualViewport?.addEventListener('resize', this.onVisualViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onVisualViewportChange);
    document.addEventListener('duna-request-scaling-update', this.onScalingUpdateRequest);
  }

  private readonly onScalingUpdateRequest = (): void => {
    this.scheduleScalingUpdate({ immediate: true });
  };

  private readonly onDocumentVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.templateService.recalculateMode();
      this.scheduleScalingUpdate();
    }
  };

  private readonly onPageShow = (): void => {
    this.templateService.recalculateMode();
    this.scheduleScalingUpdate();
  };

  private readonly onVisualViewportChange = (): void => {
    this.scheduleScalingUpdate({ immediate: true });
  };

  private setupScalableHostMutationObserver(): void {
    this.scalableHostMutationObserver?.disconnect();

    this.scalableHostMutationObserver = new MutationObserver(() => {
      if (this.el.nativeElement.querySelector('.scalable-grid-item-host')) {
        this.scheduleScalingUpdate({ immediate: true });
      }
    });

    this.scalableHostMutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  private scheduleScalingUpdate(options: { immediate?: boolean } = {}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.scalingUpdateTimeout) {
      clearTimeout(this.scalingUpdateTimeout);
    }

    const delay = options.immediate ? 0 : 16;

    this.scalingUpdateTimeout = setTimeout(() => {
      this.scalingUpdateTimeout = null;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.applyScaling());
      });
    }, delay);
  }

  private applyScaling(): void {
    const { host, target, isAppShell } = this.getScalingElements();
    const viewportWidth = this.getViewportWidth();

    if (!target || !host || viewportWidth < 100) {
      this.retryScalingLater();
      return;
    }

    this.scalingRetryCount = 0;
    this.observeScalableHost(host);

    // host.clientWidth is the only reliable source: it reflects the actual content area
    // of the scroll container, subtracting the scrollbar-gutter that scrollbar-gutter:stable
    // always reserves. window.innerWidth / document.documentElement.clientWidth do NOT
    // account for the host's gutter, causing content to slide under the scrollbar.
    const availableWidth = host.clientWidth > 0 ? host.clientWidth : viewportWidth;

    const currentMode = this.getModeForViewport(availableWidth);
    let scale = 1;

    if (currentMode === 3) {
      scale = availableWidth / this.mobileDesignWidth;
    } else if (currentMode === 2) {
      scale = availableWidth / this.tabletDesignWidth;
    }

    this.clearScalingStyles(host);
    this.clearScalingStyles(target);
    this.clearLegacyTabScaling(host);
    this.clearFluidTabsLayout(host);

    // Sync mode class on body so child components can react to mobile/tablet/desktop via CSS
    document.body.classList.toggle('duna-mode-1', currentMode === 1);
    document.body.classList.toggle('duna-mode-2', currentMode === 2);
    document.body.classList.toggle('duna-mode-3', currentMode === 3);

    // Keep TemplateService in sync: it uses window.innerWidth while we use host.clientWidth,
    // so they can disagree at the boundary (e.g. 480px + scrollbar gutter). By pushing the
    // authoritative mode here, all subscribers (dynamic-table, etc.) stay consistent.
    this.templateService.setMode(currentMode);

    if (scale !== 1) {
      const designWidth = currentMode === 3 ? this.mobileDesignWidth : this.tabletDesignWidth;
      const mainGrid = host.querySelector('#main');
      const hasDynamicTabs = !!mainGrid?.querySelector('app-dynamic-tabs');

      if (hasDynamicTabs && mainGrid) {
        // Transform scaling breaks Material tab header clicks/pagination — use fluid width instead
        this.applyFluidTabsLayout(host, target, mainGrid as HTMLElement);
      } else {
        this.applyWholeTargetScaling(target, scale, designWidth);
      }
    } else {
      this.clearFluidTabsLayout(host);
      this.renderer.removeClass(target, 'duna-is-scaled');
      this.renderer.removeStyle(target, '--duna-scale-factor');
      if (isAppShell) {
        this.renderer.setStyle(target, 'width', '100%');
      } else {
        this.renderer.removeStyle(target, 'width');
      }
    }

    if (isAppShell) {
      requestAnimationFrame(() => this.syncNavShellMetrics());
    }
  }

  /** Tablet/mobile layout for pages with dynamic-tabs: no CSS transform anywhere. */
  private applyFluidTabsLayout(host: HTMLElement, surface: HTMLElement, mainGrid: HTMLElement): void {
    const html = document.documentElement;
    const body = document.body;

    html.classList.add('duna-tabs-no-scale');
    body.classList.add('duna-tabs-no-scale');
    this.renderer.addClass(surface, 'duna-tabs-no-scale');
    this.renderer.addClass(host, 'duna-tabs-no-scale-host');
    this.renderer.setStyle(surface, 'width', '100%');
    this.renderer.removeStyle(surface, 'transform');
    this.renderer.removeStyle(surface, 'transform-origin');
    this.renderer.removeStyle(surface, 'margin-bottom');
    this.renderer.removeStyle(host, 'transform');
    this.renderer.removeStyle(host, 'transform-origin');

    mainGrid.querySelectorAll('.grid-item').forEach(node => {
      const gridItem = node as HTMLElement;
      gridItem.classList.add('duna-tabs-fluid-item');
      this.renderer.setStyle(gridItem, 'width', '100%');
      this.renderer.setStyle(gridItem, 'max-width', '100%');
      this.renderer.removeStyle(gridItem, 'transform');
      this.renderer.removeStyle(gridItem, 'transform-origin');
      this.renderer.removeStyle(gridItem, 'margin-bottom');
    });

    this.stripTransformsAboveTabs(host);
  }

  private clearFluidTabsLayout(host: HTMLElement): void {
    document.documentElement.classList.remove('duna-tabs-no-scale');
    document.body.classList.remove('duna-tabs-no-scale');

    host.querySelectorAll('.duna-tabs-no-scale, .duna-tabs-no-scale-host, .duna-tabs-fluid-item').forEach(node => {
      const element = node as HTMLElement;
      element.classList.remove('duna-tabs-no-scale', 'duna-tabs-no-scale-host', 'duna-tabs-fluid-item');
      this.renderer.removeStyle(element, 'width');
      this.renderer.removeStyle(element, 'max-width');
      this.renderer.removeStyle(element, 'transform');
      this.renderer.removeStyle(element, 'transform-origin');
      this.renderer.removeStyle(element, 'margin-bottom');
    });
  }

  /** Transform on ancestors breaks Material tab header hit-testing on desktop. */
  private stripTransformsAboveTabs(host: HTMLElement): void {
    host.querySelectorAll('app-dynamic-tabs').forEach(tabsEl => {
      let current = tabsEl.parentElement;

      while (current && !current.classList.contains('scalable-grid-item-host')) {
        this.renderer.removeStyle(current, 'transform');
        this.renderer.removeStyle(current, 'transform-origin');
        current = current.parentElement;
      }
    });
  }

  private applyWholeTargetScaling(target: HTMLElement, scale: number, designWidth: number): void {
    this.renderer.addClass(target, 'duna-is-scaled');
    this.renderer.setStyle(target, '--duna-scale-factor', String(scale));
    this.renderer.setStyle(target, 'width', `${designWidth}px`);
    this.renderer.setStyle(target, 'transform', `scale(${scale})`);
    this.renderer.setStyle(target, 'transform-origin', 'top left');
    this.syncScaledElementLayout(target, scale);
  }

  /** Remove orphaned classes from earlier split-scaling experiments. */
  private clearLegacyTabScaling(host: HTMLElement): void {
    host.querySelectorAll(
      '.duna-grid-item-scaled, .duna-has-tabs, .duna-tabs-header-unscaled, .duna-tabs-body-scaled, .duna-tabs-body-shell, .duna-tabs-content-scaled, .duna-split-scaled'
    ).forEach(node => {
      const element = node as HTMLElement;
      element.classList.remove(
        'duna-grid-item-scaled',
        'duna-has-tabs',
        'duna-tabs-header-unscaled',
        'duna-tabs-body-scaled',
        'duna-tabs-body-shell',
        'duna-tabs-content-scaled',
        'duna-split-scaled'
      );
      this.clearScalingStyles(element);
    });
  }

  private clearScalingStyles(element: HTMLElement): void {
    this.renderer.removeClass(element, 'duna-is-scaled');
    this.renderer.removeStyle(element, '--duna-scale-factor');
    this.renderer.removeStyle(element, 'transform');
    this.renderer.removeStyle(element, 'transform-origin');
    this.renderer.removeStyle(element, 'margin-bottom');
    this.renderer.removeStyle(element, 'height');
    this.renderer.removeStyle(element, 'width');
    this.renderer.removeStyle(element, 'max-width');
    this.renderer.removeStyle(element, 'box-sizing');
  }

  private syncScaledElementLayout(target: HTMLElement, scale: number): void {
    const apply = (): void => {
      const layoutHeight = target.scrollHeight || target.offsetHeight;
      if (layoutHeight <= 0) {
        return;
      }

      this.renderer.setStyle(target, 'margin-bottom', `${layoutHeight * (scale - 1)}px`);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(apply);
    });
  }

  private getScalingElements(): {
    host: HTMLDivElement | null;
    target: HTMLDivElement | null;
    isAppShell: boolean;
  } {
    const host = this.el.nativeElement.querySelector('.scalable-grid-item-host') as HTMLDivElement | null;
    const isAppShell = document.body.classList.contains('duna-app-shell');

    if (!host) {
      return { host: null, target: null, isAppShell };
    }

    if (isAppShell) {
      const surface = host.querySelector('.duna-scale-surface') as HTMLDivElement | null;
      return {
        host,
        target: surface ?? host.querySelector('.dynamic-template-container') as HTMLDivElement | null ?? host,
        isAppShell
      };
    }

    return { host, target: host, isAppShell: false };
  }

  private observeScalableHost(element: HTMLDivElement): void {
    this.scalableHostResizeObserver?.disconnect();

    this.scalableHostResizeObserver = new ResizeObserver(() => {
      this.scheduleScalingUpdate({ immediate: true });
    });

    this.scalableHostResizeObserver.observe(element);

    const surface = element.querySelector('.duna-scale-surface');
    if (surface instanceof HTMLElement) {
      this.scalableHostResizeObserver.observe(surface);
    }

    const mainGrid = element.querySelector('#main');
    if (mainGrid instanceof HTMLElement) {
      this.scalableHostResizeObserver.observe(mainGrid);
    }

    element.querySelectorAll('app-dynamic-tabs').forEach(tabs => {
      if (tabs instanceof HTMLElement) {
        this.scalableHostResizeObserver!.observe(tabs);
      }
    });
  }

  private retryScalingLater(): void {
    if (this.scalingRetryCount >= this.maxScalingRetries) {
      return;
    }

    this.scalingRetryCount += 1;

    if (this.scalingRetryTimeout) {
      clearTimeout(this.scalingRetryTimeout);
    }

    this.scalingRetryTimeout = setTimeout(() => {
      this.scalingRetryTimeout = null;
      this.scheduleScalingUpdate({ immediate: true });
    }, 50);
  }

  private getViewportWidth(): number {
    // document.documentElement.clientWidth excludes the scrollbar width (unlike window.innerWidth),
    // which prevents scaled content from sliding under the scrollbar-gutter reserved by CSS.
    return Math.round(
      document.documentElement.clientWidth
      || window.innerWidth
      || document.body.clientWidth
      || 0
    );
  }

  private getModeForViewport(viewportWidth: number): 1 | 2 | 3 {
    if (viewportWidth <= this.mobileScalingEndViewport) {
      return 3;
    }

    if (viewportWidth <= this.tabletDesignWidth) {
      return 2;
    }

    return 1;
  }
}
